import React, { useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import { isUnauthorizedAccessError } from '../../../common/isUnauthorizedAccessError';
import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useFlags } from '../../context/FlagsProviderContext';
import { getClosedEventAttributes } from '../../effects/getEventAttributes';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import {
	getInterimSupportingContent,
	isSourceContentDiverged,
	setSupportingContent,
} from '../../state/AudioPlayerState';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { Playlist } from '../playlist/Playlist';

import { AudioFeedbackFooter } from './AudioFeedbackFooter';
import { AudioHeaderPanel } from './AudioHeaderPanel';
import { AudioPlayerError } from './AudioPlayerError';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniplayerControls } from './controls/MiniplayerControls';
import type { AudioFeatureMenuItem } from './menu/AudioFeatureMenuGroup';
import { OverflowMenu } from './menu/OverflowMenu';
import { PanelWrapper } from './menu/PanelWrapper';
import { ModalHeader } from './ModalHeader';
import { UnauthorizedAccessError } from './UnauthorizedAccessError';

export const MANAGE_SOURCES_TITLE_ID = 'audio.audio-features.manage-sources-menu-item-label';

const styles = cssMap({
	expandedAudioPlayerContainer: {
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	audioPlayerMinHeight: {
		minHeight: '470px',
	},
	audioPlayerWithPendingContent: {
		height: '532px',
	},
	controlsContainer: {
		height: '404px',
	},
});

const dynamicStyles = {
	background: `linear-gradient(180deg, ${token('elevation.surface')} 50%, ${token('elevation.surface.sunken')} 100%)`,
	width: `${AUDIO_CONTAINER_MODES.EXPANDED.width}px`,
};

const messages = defineMessages({
	infoFlagTitle: {
		id: 'ai-audio.audio-player.remove-info-title',
		defaultMessage: 'Sources will not be added.',
		description: 'Title for info flag when sources are changed',
	},
	infoFlagDescription: {
		id: 'ai-audio.audio-player.remove-info-description',
		defaultMessage: 'Select Re-start briefing to add.',
		description: 'Description for info flag when sources are changed',
	},
});

type ExpandedAudioPlayerProps = {
	audioFeatures: AudioFeature[];
	userId?: string;
	displayName?: string;
};

export const ExpandedAudioPlayer = ({
	audioFeatures,
	userId,
	displayName,
}: ExpandedAudioPlayerProps): React.JSX.Element => {
	const {
		activeContent,
		activeFeature,
		cloudId,
		activeMenu,
		closeAudioPlayer,
		pendingContent,
		setActiveMenu,
		playlistDefaultExpanded,
	} = useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { audioRef } = useAudioPlayerStreamingContext();
	const { trackAIResultAction } = useAIEventsInstrumentation();
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const { formatMessage } = useIntl();
	const flags = useFlags();

	const activeFeatureInfo = getFeatureInfo(activeFeature, audioFeatures);

	const hasOverflowMenu =
		activeFeatureInfo?.menuConfig?.items && activeFeatureInfo?.menuConfig?.items.length > 0;

	const isErrorState = streamingState.state === AudioStreamingState.ERROR;
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;
	const isUnauthorizedAccess = isErrorState && isUnauthorizedAccessError(streamingState.error);
	const overflowMenuItems: AudioFeatureMenuItem[] = (
		activeFeatureInfo?.menuConfig?.items ?? []
	).map((item) => {
		return {
			...item,
			// Disable the manage sources menu item if it exists and there is no content selected
			isDisabled:
				(item.title.id === MANAGE_SOURCES_TITLE_ID && !activeContent?.contentId) || item.isDisabled,
		};
	});

	useEffect(() => {
		// Focus the menu button when the expanded audio player opens (only when not showing a panel)
		if (!activeMenu && hasOverflowMenu && menuButtonRef.current) {
			menuButtonRef.current.focus();
		} else if (!activeMenu && !hasOverflowMenu && closeButtonRef.current) {
			// Fallback to close button if there's no overflow menu
			closeButtonRef.current.focus();
		}
	}, [activeMenu, hasOverflowMenu]);

	const onBack = () => {
		const interimSupportingContent = getInterimSupportingContent() ?? [];
		const sourcesDiverged = isSourceContentDiverged(
			activeContent.supportingContent,
			interimSupportingContent,
		);
		if (activeMenu?.id === 'manageSources' && sourcesDiverged) {
			if (isUninitialized) {
				setSupportingContent(interimSupportingContent);
			} else {
				flags?.showFlag({
					appearance: 'info',
					title: formatMessage(messages.infoFlagTitle),
					description: formatMessage(messages.infoFlagDescription),
					close: 'auto',
				});
			}
		}
		setActiveMenu(undefined);
	};

	const handleClose = () => {
		trackAIResultAction('audioClosed', {
			attributes: {
				...getClosedEventAttributes(audioRef, 'closed from expanded audio player'),
			},
		});
		closeAudioPlayer();
	};

	if (activeMenu && activeMenu.renderPanel) {
		return (
			<Box xcss={styles.expandedAudioPlayerContainer} style={dynamicStyles}>
				<PanelWrapper header={activeMenu.renderPanel.header} onBack={onBack}>
					{activeMenu.renderPanel?.body()}
				</PanelWrapper>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.expandedAudioPlayerContainer,
				styles.audioPlayerMinHeight,
				pendingContent && !isUninitialized && styles.audioPlayerWithPendingContent,
			)}
			style={dynamicStyles}
		>
			<ModalHeader
				leftElement={
					hasOverflowMenu ? (
						<OverflowMenu items={overflowMenuItems} buttonRef={menuButtonRef} />
					) : undefined
				}
				onClose={handleClose}
				closeButtonRef={closeButtonRef}
			/>
			<Stack spread="space-between" xcss={styles.controlsContainer}>
				<Stack space="space.250" alignInline="center">
					{!isUnauthorizedAccess && (
						<AudioHeaderPanel featureList={audioFeatures} cloudId={cloudId} />
					)}
					{isUnauthorizedAccess ? (
						<UnauthorizedAccessError />
					) : isErrorState ? (
						<Flex justifyContent="center">
							<AudioPlayerError error={streamingState.error} />
						</Flex>
					) : (
						<>
							<MiniplayerControls />
							{!isUninitialized && !pendingContent && <AudioTimeIndicator />}
						</>
					)}
				</Stack>
			</Stack>
			<AudioFeedbackFooter userId={userId} displayName={displayName} />
			<Playlist cloudId={cloudId} defaultExpanded={playlistDefaultExpanded} />
		</Box>
	);
};
